import { useEffect, type ReactNode } from 'react';
import { Alert, Form } from 'react-bootstrap';
import { get, type FieldValues, type UseFormReturn } from 'react-hook-form';
import { fieldErrors } from '../api/client';
import type { Option } from '../api/types';
import { DateInput, type DateInputMode } from '../ui/DateInput';

/**
 * The slice of react-hook-form's API the field components use. A structural type (instead of
 * UseFormReturn<any>) so a form typed for one screen's values can be passed to shared components.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
export type AnyForm = Pick<UseFormReturn<any>, 'register' | 'formState' | 'watch' | 'setValue' | 'getValues' | 'setError'> & {
  control: { _fields: unknown };
};
/* eslint-enable @typescript-eslint/no-explicit-any */

interface Common {
  form: AnyForm;
  name: string;
  label: string;
  help?: ReactNode;
  disabled?: boolean;
  required?: boolean;
  className?: string;
}

const errorOf = (form: AnyForm, name: string): string | undefined => {
  const e = get(form.formState.errors, name);
  return typeof e?.message === 'string' ? e.message : undefined;
};

function Group({ name, label, help, required, className, error, children }: Pick<Common, 'name' | 'label' | 'help' | 'required' | 'className'> & { error?: string; children: ReactNode }) {
  return (
    <Form.Group className={className ?? 'mb-3'} controlId={`f-${name}`}>
      <Form.Label>
        {label}
        {required && <span className="text-danger ms-1" aria-hidden>*</span>}
      </Form.Label>
      {children}
      {error ? <Form.Control.Feedback type="invalid" className="d-block">{error}</Form.Control.Feedback> : null}
      {help && !error ? <Form.Text muted>{help}</Form.Text> : null}
    </Form.Group>
  );
}

export function TextField({
  form, name, label, help, disabled, required, className, type = 'text', inputMode, placeholder, rows, autoComplete = 'off',
}: Common & { type?: string; inputMode?: 'decimal' | 'numeric' | 'text' | 'email' | 'tel'; placeholder?: string; rows?: number; autoComplete?: string }) {
  const error = errorOf(form, name);
  return (
    <Group {...{ name, label, help, required, className, error }}>
      {rows ? (
        <Form.Control {...form.register(name)} as="textarea" rows={rows} placeholder={placeholder} disabled={disabled} isInvalid={!!error} aria-required={required} />
      ) : (
        <Form.Control
          {...form.register(name)}
          type={type}
          inputMode={inputMode}
          placeholder={placeholder}
          disabled={disabled}
          isInvalid={!!error}
          autoComplete={autoComplete}
          aria-required={required}
        />
      )}
    </Group>
  );
}

/** Date / date-time input shown as DD-MM-YYYY [hh:mm AM/PM]; the form value stays the ISO string a native date input would give. */
export function DateField({ form, name, label, help, disabled, required, className, mode }: Common & { mode: DateInputMode }) {
  const error = errorOf(form, name);
  form.register(name);
  return (
    <Group {...{ name, label, help, required, className, error }}>
      <DateInput
        id={`f-${name}`}
        mode={mode}
        value={String(form.watch(name) ?? '')}
        disabled={disabled}
        isInvalid={!!error}
        aria-required={required}
        onChange={(v) => form.setValue(name, v, { shouldDirty: true, shouldValidate: !!error })}
      />
    </Group>
  );
}

/** Money/rate input: text with a decimal keypad; the schema does the real validation. */
export const MoneyField = (p: Omit<Parameters<typeof TextField>[0], 'type' | 'inputMode'>) => (
  <TextField {...p} inputMode="decimal" />
);

/**
 * Mobile number input: strips anything that isn't a digit and caps the length at 10 as the user
 * types/pastes, so entering letters or symbols is not just rejected on submit but never lands in the
 * field at all. The schema (mobileReq/mobileOpt) still enforces exactly 10 digits server-round-trip.
 */
/** Live-sanitizes to digits only (and caps length); `maxLength` defaults to 10 for a standard mobile number. */
export function MobileField({ form, name, label, help, disabled, required, className, placeholder, maxLength = 10 }: Common & { placeholder?: string; maxLength?: number }) {
  const error = errorOf(form, name);
  const { onChange, ...registered } = form.register(name);
  return (
    <Group {...{ name, label, help, required, className, error }}>
      <Form.Control
        {...registered}
        type="tel"
        inputMode="numeric"
        maxLength={maxLength}
        placeholder={placeholder}
        disabled={disabled}
        isInvalid={!!error}
        autoComplete="tel"
        aria-required={required}
        onChange={(e) => {
          e.target.value = e.target.value.replace(/\D/g, '').slice(0, maxLength);
          onChange(e);
        }}
      />
    </Group>
  );
}

export function SelectField({
  form, name, label, help, disabled, required, className, options, placeholder = 'Select…',
}: Common & { options: Option[]; placeholder?: string }) {
  const error = errorOf(form, name);
  // A <select> can only show a value whose <option> exists. If the options arrive after the value was set
  // (lookups still loading when a saved record opens), re-apply the value once they are there.
  const optionKey = options.map((o) => o.value).join('|');
  useEffect(() => {
    const v = form.getValues(name);
    if (v !== undefined && v !== null && v !== '') form.setValue(name, v);
  }, [optionKey]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Group {...{ name, label, help, required, className, error }}>
      <Form.Select {...form.register(name)} disabled={disabled} isInvalid={!!error} aria-required={required}>
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </Form.Select>
    </Group>
  );
}

export function CheckField({ form, name, label, help, disabled, className }: Omit<Common, 'required'>) {
  return (
    <Form.Group className={className ?? 'mb-3'} controlId={`f-${name}`}>
      <Form.Check type="switch" label={label} {...form.register(name)} disabled={disabled} />
      {help ? <Form.Text muted>{help}</Form.Text> : null}
    </Form.Group>
  );
}

/** Shows the message of a failed save that is not tied to a single field. */
export function FormError({ message }: { message: string | null }) {
  if (!message) return null;
  return <Alert variant="danger" className="py-2" role="alert">{message}</Alert>;
}

/**
 * Puts server-side validation messages on the matching form fields.
 * Returns a message to display when the error is not field-specific (or has no matching field).
 */
export function applyServerErrors(form: AnyForm, err: unknown): string | null {
  const list = fieldErrors(err);
  let unmatched = false;
  for (const { path, message } of list) {
    // only attach to fields that are actually rendered; anything else is shown as a general message
    if (path && get(form.control._fields, path)) {
      form.setError(path as never, { type: 'server', message });
    } else {
      unmatched = true;
    }
  }
  if (list.length > 0 && !unmatched) return null;
  if (list.length > 0) return list.map((l) => l.message).join('; ');
  return err instanceof Error ? err.message : 'Something went wrong';
}

export type { FieldValues };
